import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Smartphone } from 'lucide-react';
import { isMobile, isStandalone, getTargetPlatform } from '../utils/deviceDetection';
import { getStoreUrl } from '../config/appStoreConfig';
import { analytics } from '../analytics';

const STORAGE_KEY = 'pickify_app_banner_dismissed';
const DISMISS_DAYS = 30;
const SCROLL_THRESHOLD = 200;
const PLACEMENT = 'sticky_banner';

/**
 * AppDownloadStickyBanner — slim, dismissible bottom bar on mobile only.
 *
 * Design rules (informed by Google's intrusive-interstitial guidelines and
 * Branch.io smart-banner benchmarks):
 *   - mobile-only (desktop has the footer + results card)
 *   - takes < 15% of viewport height
 *   - clearly dismissible
 *   - 30-day cooldown after dismiss
 *   - hidden on /admin, during quiz/results (those have stronger placements)
 *   - hidden when installed as standalone (already has the app feel)
 */
const AppDownloadStickyBanner = () => {
  const location = useLocation();
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState(null);

  useEffect(() => {
    if (!isMobile() || isStandalone()) return;

    // Path-based suppression (admin should never see CTAs)
    if (location.pathname === '/admin') return;

    // On the home page, defer to the inline post-results card during quiz/results
    if (location.pathname === '/') {
      const quizActive = localStorage.getItem('pickify_quiz_progress');
      const quizComplete = localStorage.getItem('pickify_quiz_complete');
      if (quizActive || quizComplete) return;
    }

    // Respect prior dismissal
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const dismissedAt = new Date(raw);
        const days = (Date.now() - dismissedAt.getTime()) / (1000 * 60 * 60 * 24);
        if (days < DISMISS_DAYS) return;
      }
    } catch { /* localStorage unavailable */ }

    setPlatform(getTargetPlatform());

    // Reveal after scroll engagement — banner blindness research shows
    // scroll-triggered banners outperform on-load ones.
    const onScroll = () => {
      if (window.scrollY > SCROLL_THRESHOLD) {
        setVisible(true);
        window.removeEventListener('scroll', onScroll);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    // Fallback: reveal after 12s of dwell
    const timer = setTimeout(() => setVisible(true), 12000);

    return () => {
      window.removeEventListener('scroll', onScroll);
      clearTimeout(timer);
    };
  }, [location.pathname]);

  const handleDismiss = (e) => {
    e?.stopPropagation?.();
    setVisible(false);
    try {
      localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    } catch { /* localStorage unavailable */ }
    analytics.appDownloadDismissed(PLACEMENT);
  };

  const handleClick = () => {
    if (!platform || platform === 'desktop') return;
    analytics.appDownloadClicked(PLACEMENT, platform);
  };

  if (!visible || !platform || platform === 'desktop') return null;

  const storeUrl = getStoreUrl(platform, PLACEMENT);
  const ctaLabel = platform === 'ios' ? 'App Store' : 'Google Play';

  return (
    <div
      className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] animate-slide-up"
      role="complementary"
      aria-label="Get the Pickify app"
    >
      <div className="flex items-center gap-3 px-3 py-2.5 pr-20">
        <button
          onClick={handleDismiss}
          className="flex-shrink-0 p-1.5 -ml-1 text-gray-500 hover:text-gray-700 transition-colors"
          aria-label="Dismiss app download banner"
        >
          <X size={18} />
        </button>

        <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-sm">
          <Smartphone className="text-white" size={20} />
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-gray-900 leading-tight truncate">Get the Pickify app</p>
          <p className="text-xs text-gray-500 leading-tight truncate">Save picks, get deal alerts</p>
        </div>

        <a
          href={storeUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
          className="flex-shrink-0 px-3.5 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold rounded-lg text-xs shadow hover:shadow-md transition-all whitespace-nowrap"
        >
          {ctaLabel}
        </a>
      </div>

      <style>{`
        @keyframes app-banner-slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up {
          animation: app-banner-slide-up 0.28s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AppDownloadStickyBanner;
